import React from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Typography } from '@material-ui/core';
import { Add, DeleteOutline } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { etichettaRecapito } from './contattiNotificheUtils';

const useStyles = makeStyles((theme) => ({
  blocco: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    textAlign: 'left',
  },
  intestazione: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  riceve: {
    color: theme.palette.text.secondary,
  },
  elenco: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  riga: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  recapito: {
    display: 'flex',
    flexDirection: 'column',
  },
  indirizzo: {
    color: theme.palette.text.secondary,
  },
  vuoto: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

/**
 * The recipients of one scope, listed so that «who gets the next escalation» is
 * readable without opening anything (acceptance criterion of BSDEVPLAT-112).
 *
 * An empty scope says so in words: a table with no rows reads as «not configured
 * yet», which is exactly the silence the ticket asks to break.
 *
 * Every control is disabled while a write is in flight against this scope: the
 * routes are idempotent, so a double click cannot corrupt anything, but the
 * second request would race the re-read that follows the first.
 */
const ElencoRecapitiScope = ({
  titolo,
  riceve,
  contatti,
  messaggioVuoto,
  etichettaAggiungi,
  onAggiungi,
  onRimuovi,
  scritturaInCorso,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.blocco}>
      <div className={classes.intestazione}>
        <Typography variant="subtitle1" component="h4">
          {titolo}
        </Typography>
        <Button
          size="small"
          color="primary"
          startIcon={<Add />}
          aria-label={etichettaAggiungi}
          disabled={scritturaInCorso}
          onClick={onAggiungi}
        >
          Aggiungi recapito
        </Button>
      </div>
      <Typography variant="body2" component="p" className={classes.riceve}>
        {riceve}
      </Typography>
      {contatti.length === 0 ? (
        <Typography variant="body2" component="p" className={classes.vuoto}>
          {messaggioVuoto}
        </Typography>
      ) : (
        <ul className={classes.elenco}>
          {contatti.map((contatto) => (
            <li key={contatto.id} className={classes.riga}>
              <span className={classes.recapito}>
                <Typography variant="body2" component="span">
                  {[contatto.nome, contatto.cognome].filter(Boolean).join(' ')}
                </Typography>
                <Typography
                  variant="caption"
                  component="span"
                  className={classes.indirizzo}
                >
                  {[contatto.email, contatto.telefono]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </span>
              <IconButton
                size="small"
                aria-label={`Rimuovi ${etichettaRecapito(contatto)}`}
                disabled={scritturaInCorso}
                onClick={() => onRimuovi(contatto)}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

ElencoRecapitiScope.propTypes = {
  titolo: PropTypes.string.isRequired,
  // What this scope receives, in words: it is the answer to «chi riceve cosa».
  riceve: PropTypes.string.isRequired,
  contatti: PropTypes.arrayOf(PropTypes.object).isRequired,
  messaggioVuoto: PropTypes.string.isRequired,
  // The accessible name of the add button. The visible label is the same on
  // every scope, so on a screen holding one struttura block and one per reparto
  // the button needs to say which scope it adds to.
  etichettaAggiungi: PropTypes.string,
  onAggiungi: PropTypes.func.isRequired,
  onRimuovi: PropTypes.func.isRequired,
  scritturaInCorso: PropTypes.bool,
};

export default ElencoRecapitiScope;
